import type { Position, Size, ConnectionPointPosition, CanvasElement } from '@/types/thinkspace';

/**
 * Get the connection point positions for an element based on its bounds
 */
export function getConnectionPoints(
  position: Position,
  size: Size
): Record<ConnectionPointPosition, Position> {
  const { x, y } = position;
  const { width, height } = size;

  return {
    'top': { x: x + width / 2, y: y },
    'right': { x: x + width, y: y + height / 2 },
    'bottom': { x: x + width / 2, y: y + height },
    'left': { x: x, y: y + height / 2 },
    'top-left': { x: x, y: y },
    'top-right': { x: x + width, y: y },
    'bottom-left': { x: x, y: y + height },
    'bottom-right': { x: x + width, y: y + height },
  };
}

/**
 * Get connection points for ellipse/circle (4 cardinal points only)
 */
export function getEllipseConnectionPoints(
  position: Position,
  size: Size
): Record<ConnectionPointPosition, Position> {
  const { x, y } = position;
  const { width, height } = size;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const rx = width / 2;
  const ry = height / 2;

  return {
    'top': { x: cx, y: y },
    'right': { x: x + width, y: cy },
    'bottom': { x: cx, y: y + height },
    'left': { x: x, y: cy },
    'top-left': { x: cx - rx * 0.707, y: cy - ry * 0.707 },
    'top-right': { x: cx + rx * 0.707, y: cy - ry * 0.707 },
    'bottom-left': { x: cx - rx * 0.707, y: cy + ry * 0.707 },
    'bottom-right': { x: cx + rx * 0.707, y: cy + ry * 0.707 },
  };
}

/**
 * Get connection points for diamond shape
 */
export function getDiamondConnectionPoints(
  position: Position,
  size: Size
): Record<ConnectionPointPosition, Position> {
  const { x, y } = position;
  const { width, height } = size;
  const cx = x + width / 2;
  const cy = y + height / 2;

  // Diamond points are at the midpoints of edges
  return {
    'top': { x: cx, y: y },
    'right': { x: x + width, y: cy },
    'bottom': { x: cx, y: y + height },
    'left': { x: x, y: cy },
    // Corners don't really exist for diamond, use midpoints
    'top-left': { x: x + width * 0.25, y: y + height * 0.25 },
    'top-right': { x: x + width * 0.75, y: y + height * 0.25 },
    'bottom-left': { x: x + width * 0.25, y: y + height * 0.75 },
    'bottom-right': { x: x + width * 0.75, y: y + height * 0.75 },
  };
}

/**
 * Get the element bounds (position and size) for any element type
 */
export function getElementBounds(element: CanvasElement): { position: Position; size: Size } | null {
  switch (element.type) {
    case 'postit':
    case 'box':
    case 'ellipse':
    case 'diamond':
      return { position: element.position, size: element.size };
    case 'text':
      // Text doesn't have a fixed size, estimate based on content
      return {
        position: element.position,
        size: { width: 100, height: 30 } // Approximate
      };
    case 'line':
      // Lines connect from position to endPosition
      const minX = Math.min(element.position.x, element.endPosition.x);
      const minY = Math.min(element.position.y, element.endPosition.y);
      const maxX = Math.max(element.position.x, element.endPosition.x);
      const maxY = Math.max(element.position.y, element.endPosition.y);
      return {
        position: { x: minX, y: minY },
        size: { width: maxX - minX || 10, height: maxY - minY || 10 }
      };
    case 'connector':
      return null; // Connectors don't have connection points
    default:
      return null;
  }
}

/**
 * Get connection points for any element type
 */
export function getElementConnectionPoints(
  element: CanvasElement
): Record<ConnectionPointPosition, Position> | null {
  const bounds = getElementBounds(element);
  if (!bounds) return null;

  switch (element.type) {
    case 'ellipse':
      return getEllipseConnectionPoints(bounds.position, bounds.size);
    case 'diamond':
      return getDiamondConnectionPoints(bounds.position, bounds.size);
    default:
      return getConnectionPoints(bounds.position, bounds.size);
  }
}

/**
 * Find the nearest connection point to a given position
 */
export function findNearestConnectionPoint(
  pos: Position,
  element: CanvasElement,
  maxDistance: number = 20
): { position: ConnectionPointPosition; point: Position } | null {
  const points = getElementConnectionPoints(element);
  if (!points) return null;

  let nearestPos: ConnectionPointPosition | null = null;
  let nearestDist = maxDistance;
  let nearestPoint: Position | null = null;

  for (const [posName, point] of Object.entries(points)) {
    const dist = Math.sqrt(
      Math.pow(pos.x - point.x, 2) + Math.pow(pos.y - point.y, 2)
    );
    if (dist < nearestDist) {
      nearestDist = dist;
      nearestPos = posName as ConnectionPointPosition;
      nearestPoint = point;
    }
  }

  return nearestPos && nearestPoint
    ? { position: nearestPos, point: nearestPoint }
    : null;
}

/**
 * Calculate angular (orthogonal) routing between two points
 * Creates a path with only 90-degree turns (Visio-style)
 */
export function calculateAngularRoute(
  start: Position,
  end: Position,
  startDirection?: 'horizontal' | 'vertical',
  margin: number = 20
): Position[] {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // If points are close to aligned, use simple routing
  if (Math.abs(dx) < margin) {
    // Vertically aligned - straight line
    return [];
  }
  if (Math.abs(dy) < margin) {
    // Horizontally aligned - straight line
    return [];
  }

  // Determine routing based on relative positions
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;

  // Default: use horizontal-first routing if start is more to the left
  // or vertical-first if start is more above
  const useHorizontalFirst = startDirection === 'horizontal' ||
    (startDirection !== 'vertical' && Math.abs(dx) > Math.abs(dy));

  if (useHorizontalFirst) {
    // Horizontal first, then vertical
    return [
      { x: midX, y: start.y },
      { x: midX, y: end.y },
    ];
  } else {
    // Vertical first, then horizontal
    return [
      { x: start.x, y: midY },
      { x: end.x, y: midY },
    ];
  }
}

/**
 * Calculate smarter angular routing that avoids overlapping the source/target elements
 */
export function calculateSmartAngularRoute(
  start: Position,
  end: Position,
  startElement?: CanvasElement | null,
  endElement?: CanvasElement | null,
  margin: number = 30
): Position[] {
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Simple cases - nearly aligned
  if (Math.abs(dx) < 10) return []; // Vertical line
  if (Math.abs(dy) < 10) return []; // Horizontal line

  // Get element bounds for smarter routing
  const startBounds = startElement ? getElementBounds(startElement) : null;
  const endBounds = endElement ? getElementBounds(endElement) : null;

  // Determine which direction to exit/enter based on positions
  const goingRight = dx > 0;
  const goingDown = dy > 0;

  // Calculate intermediate points with margin from elements
  let midX: number;
  let midY: number;

  if (startBounds && endBounds) {
    // Route around elements
    const startRight = startBounds.position.x + startBounds.size.width;
    const startBottom = startBounds.position.y + startBounds.size.height;
    const endRight = endBounds.position.x + endBounds.size.width;
    const endBottom = endBounds.position.y + endBounds.size.height;

    if (goingRight) {
      midX = Math.max(startRight + margin, endBounds.position.x - margin);
    } else {
      midX = Math.min(startBounds.position.x - margin, endRight + margin);
    }

    if (goingDown) {
      midY = Math.max(startBottom + margin, endBounds.position.y - margin);
    } else {
      midY = Math.min(startBounds.position.y - margin, endBottom + margin);
    }
  } else {
    // Simple midpoint routing
    midX = (start.x + end.x) / 2;
    midY = (start.y + end.y) / 2;
  }

  // Create routing points based on relative positions
  if (Math.abs(dx) > Math.abs(dy)) {
    // More horizontal - go horizontal first
    return [
      { x: midX, y: start.y },
      { x: midX, y: end.y },
    ];
  } else {
    // More vertical - go vertical first
    return [
      { x: start.x, y: midY },
      { x: end.x, y: midY },
    ];
  }
}

/**
 * Generate SVG path data for a connector with angular routing
 */
export function generateConnectorPath(
  start: Position,
  end: Position,
  routingPoints: Position[]
): string {
  let path = `M ${start.x} ${start.y}`;

  for (const point of routingPoints) {
    path += ` L ${point.x} ${point.y}`;
  }

  path += ` L ${end.x} ${end.y}`;

  return path;
}
